import { trackClientEvent } from 'analytics/analytics'
import { useIntl } from 'react-intl'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import SolidGoogleIcon from 'LuxKit/Icons/solid/SolidGoogleIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { SOCIAL_LOGIN_ICON_BUTTON_PROPS, SOCIAL_LOGIN_TEXT_BUTTON_PROPS, type SocialLoginButtonVariant } from './SocialLoginButton.constants'
import config from 'constants/config'
import { setPendingGoogleCollectPhone } from 'lib/auth/googlePhoneCollection'
import { cancelGoogleOneTap } from 'lib/auth/googleOneTap'
import LoadingSpinner from 'components/Common/Loading/LoadingSpinner'

const RelativeTextButton = styled(TextButton)`
  position: relative; /** so that the absolutely positioned span can sit properly */
  overflow: hidden;
`

const RelativeIconButton = styled(IconButton)`
  position: relative; /** so that the absolutely positioned span can sit properly */
  overflow: hidden;
`

const StylesSpan = styled.span`
  position: absolute;
  top: 0;
  bottom: 0;
  opacity: 0.01;
`

interface Props {
  iconOnly?: boolean
  collectPhone?: boolean
  variant?: SocialLoginButtonVariant
}

function GoogleLoginButton({ iconOnly, variant, collectPhone = false }: Props) {
  const intl = useIntl()
  const gsiLoaded = useAppSelector(state => state.auth.gsiLoaded)
  const [element, setElement] = useState<HTMLElement | null>(null)
  const renderedRef = useRef(false)
  const [isReady, setIsReady] = useState(false)

  const clickHandler = useCallback(() => {
    setPendingGoogleCollectPhone(collectPhone)
    trackClientEvent({
      subject: 'sign_up_sso_click',
      action: 'google_auth',
      category: 'logging',
      type: 'operational',
    })
  }, [collectPhone])

  // For review/canary apps, redirect to shared auth domain for Google OAuth
  const handleSharedAuthRedirect = useCallback(() => {
    clickHandler()
    const returnTo = encodeURIComponent(window.location.origin)
    window.location.href = `https://${config.GOOGLE_AUTH_REDIRECT_DOMAIN}/google/auth/init?return_to=${returnTo}`
  }, [clickHandler])

  useEffect(() => {
    // Skip Google button rendering for review/canary apps - they use shared auth domain redirect
    if (config.GOOGLE_AUTH_REDIRECT_DOMAIN) {
      return
    }

    if (!gsiLoaded || !element || !window.google?.accounts?.id) return

    // Google's iframe can already accept clicks before its load event fires.
    // Observe the actual SDK control instead of keeping a working button busy.
    const markReadyIfRendered = () => {
      if (element.querySelector('iframe')) setIsReady(true)
    }
    const observer = new MutationObserver(markReadyIfRendered)
    observer.observe(element, { childList: true, subtree: true })
    if (!renderedRef.current) {
      cancelGoogleOneTap()
      window.google.accounts.id.renderButton(
        element,
        {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          logo_alignment: 'center',
          click_listener: clickHandler,
          width: iconOnly ? 100 : 600,
        },
      )
      renderedRef.current = true
    }
    markReadyIfRendered()

    return () => observer.disconnect()
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [gsiLoaded, element])

  // For review/canary apps, render a simple button that redirects to shared auth domain
  if (config.GOOGLE_AUTH_REDIRECT_DOMAIN) {
    return <>
      {!iconOnly && <TextButton
        {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
        variant={variant ?? SOCIAL_LOGIN_TEXT_BUTTON_PROPS.variant}
        startIcon={<SolidGoogleIcon />}
        onClick={handleSharedAuthRedirect}
      >
        {intl.formatMessage({
          id: '4rPmpBhNzPmJWTR5hOIc',
          defaultMessage: 'Google',
        })}
      </TextButton>}
      {iconOnly && <IconButton
        {...SOCIAL_LOGIN_ICON_BUTTON_PROPS}
        variant={variant ?? SOCIAL_LOGIN_ICON_BUTTON_PROPS.variant}
        onClick={handleSharedAuthRedirect}
      >
        <SolidGoogleIcon />
      </IconButton>}
    </>
  }

  return <>
    {!iconOnly && <RelativeTextButton
      {...SOCIAL_LOGIN_TEXT_BUTTON_PROPS}
      variant={variant ?? SOCIAL_LOGIN_TEXT_BUTTON_PROPS.variant}
      startIcon={isReady ? <SolidGoogleIcon /> : <LoadingSpinner size={20} variant="primary" />}
      aria-busy={!isReady}
    >
      {intl.formatMessage({
        id: 'HJKMIh21gEhml9GfCLhA',
        defaultMessage: 'Google',
      })}
      <StylesSpan ref={setElement} />
    </RelativeTextButton>}
    {iconOnly && <RelativeIconButton
      {...SOCIAL_LOGIN_ICON_BUTTON_PROPS}
      variant={variant ?? SOCIAL_LOGIN_ICON_BUTTON_PROPS.variant}
      aria-busy={!isReady}
    >
      <StylesSpan ref={setElement} />
      {isReady ? <SolidGoogleIcon /> : <LoadingSpinner size={20} variant="primary" />}
    </RelativeIconButton>}
  </>
}

export default GoogleLoginButton
